import { defineStore } from 'pinia'
import { ref } from 'vue'
import { expectArray } from '../composables/expectShape'
import { useAsyncResource, UnauthorizedError } from '../composables/useAsyncResource'
import type { ProductOwnerReviewAggregateRow, ProductOwnerReviewRepoRow } from './product_owner_review_rows'

// Re-exported so the Review tab's importers keep one source for every review row type.
export type { ProductOwnerReviewAggregateRow, ProductOwnerReviewRepoRow } from './product_owner_review_rows'

export interface ProductOwnerRun {
  id: number
  repo: string
  status: string
  summary: string | null
  error: string | null
  cost_cents: number | null
  merged_pr_numbers: number[]
  auto_merge_notes: string | null
  made_progress: boolean
  // Human-filed open-issue count (excludes `filed-by-agent`) at run start
  // and run end, and the after-minus-before delta — null whenever a count
  // wasn't recorded (e.g. `backlog_after` on a run that failed before
  // reaching it). `net_progress` (#1623) is what actually shrank the
  // backlog or merged a PR that closed an issue — distinct from
  // `made_progress`, which stays true on a bare self-report or any merge.
  backlog_before: number | null
  backlog_after: number | null
  backlog_delta: number | null
  // Same three figures over every open issue, `filed-by-agent` included.
  total_backlog_before: number | null
  total_backlog_after: number | null
  total_backlog_delta: number | null
  net_progress: boolean
  // True for a `running` row old enough that the backend presumes it was
  // abandoned (same cutoff ProductOwnerRun.busy?/.reap_stale! use); optional
  // so fixtures needn't carry a field that's absent in every other case.
  stale?: boolean
  // Elapsed seconds for a `running` row, null otherwise (#1739).
  running_for_seconds?: number | null
  started_at: string | null
  finished_at: string | null
  created_at: string
  // Only present on `latest_per_repo` entries, not the flat `runs` history
  // list — a forward-looking per-repo fact, not a property of one run
  // (#1344). `at` is null except for 'scheduled' and 'held' (#1744, when the hold lifts).
  next_check?: { state: 'disabled' | 'running' | 'overrun' | 'paused' | 'held' | 'scheduled'; at: string | null } | null
  // latest_per_repo only; null for a repo unknown to the registry.
  cadence?: ProductOwnerCadence | null
}

// `resulting_interval_minutes` is what the next finished run's per-run step
// will actually apply — idle always relaxes and bursting never moves until
// `settle`, regardless of today's raw count/baseline trend (#1665).
export interface ProductOwnerCadence {
  current_interval_minutes: number
  today_count: number | null
  baseline_count: number | null
  relative_change: number | null
  max_interval_minutes: number
  resulting_interval_minutes: number | null
  // Which mechanism last moved the interval ('recompute' / 'burst' / 'idle'
  // / 'max_lowered') and when — null until something has moved it (#1459).
  last_adjusted_by: string | null
  last_adjusted_at: string | null
  // AgentRepo's persisted aasm cadence state (#1536) — null for a repo with
  // no db row yet, distinct from the 'unstarted' default a row starts in.
  state: string | null
  // When `state` was last actually entered (#1625) — null until the first real transition.
  state_entered_at: string | null
  // The run currently holding the repo, if any, and whether it is past the
  // interval (#1739).
  in_flight?: { run_id: number; running_for_seconds: number; overrun: boolean } | null
}

// One day's cell in the Activity tab's contribution-style heatmap (#1036) —
// aggregated across every opted-in repo, not scoped to one.
export interface ProductOwnerActivityDay {
  date: string
  total_runs: number
  idle_runs: number
  idle_percentage: number
}

// One hour's cell in the Activity tab's per-repo drill-down (#1210) —
// scoped to a single repo, unlike ProductOwnerActivityDay.
export interface ProductOwnerActivityHour {
  hour: string
  total_runs: number
  idle_runs: number
  idle_percentage: number
}

// Windowed headline totals for the Review tab (#1183). Rate/average fields
// are null (not 0) when their denominator is zero — "nothing ran" and
// "nothing idled/failed" are different facts.
export interface ProductOwnerReviewTotals {
  runs: number
  total_spend_cents: number
  merged_pr_count: number
  cost_per_merged_pr_cents: number | null
  failure_rate: number | null
  idle_rate: number | null
}

// One day's spend/merged-PR bar in the Review tab's daily charts (#1192) —
// aggregated across every repo, same as the Activity heatmap's days.
export interface ProductOwnerReviewDay {
  date: string
  spend_cents: number
  merged_pr_count: number
}

// Cost distribution over concluded runs' cost_cents (#1192) — every field
// nil when the window's cost sample is empty, not a division-by-zero
// artifact.
export interface ProductOwnerReviewCostPercentiles {
  p50: number | null
  p75: number | null
  p90: number | null
  p95: number | null
  p99: number | null
  max: number | null
  top_5_percent_share: number | null
}

// Admin store for the product-owner heartbeat's run history (#916) —
// read-only, no mutations, mirroring useAgentHostStatsStore's fetch/loading/error shape.
//
// Each of the 4 fetchers below owns an independent `useAsyncResource` call
// (and its own `needsAuth`, per #1529) rather than sharing one lifecycle —
// they hit different endpoints on different schedules (poll / tab-mount /
// selector-change) and nothing downstream reads a combined signal.
export const useProductOwnerRunsStore = defineStore('productOwnerRuns', () => {
  // One row per repo — its latest run — so a repo that runs less often isn't
  // pushed out of `runs`' flat top-N window by a noisier one (issue #957).
  const latestPerRepo = ref<ProductOwnerRun[]>([])
  const {
    data: runs,
    loaded,
    loading,
    error: loadError,
    needsAuth,
    refresh: fetchRuns,
  } = useAsyncResource<ProductOwnerRun[]>(
    async () => {
      const res = await fetch('/api/v1/agent/product_owner_runs', { credentials: 'include' })
      if (res.status === 401) throw new UnauthorizedError()
      if (!res.ok) throw new Error(`Failed to load product-owner runs: ${res.status}`)
      const body = (await res.json()) as { runs: ProductOwnerRun[]; latest_per_repo?: ProductOwnerRun[] }
      latestPerRepo.value = expectArray<ProductOwnerRun>(
        body.latest_per_repo ?? [],
        'product-owner runs (latest per repo)',
      )
      return expectArray<ProductOwnerRun>(body.runs, 'product-owner runs')
    },
    { initialValue: [] },
  )

  const {
    data: activityDays,
    loaded: activityLoaded,
    loading: activityLoading,
    error: activityLoadError,
    needsAuth: activityNeedsAuth,
    refresh: fetchActivity,
  } = useAsyncResource<ProductOwnerActivityDay[]>(
    async () => {
      const res = await fetch('/api/v1/agent/product_owner_runs/activity', { credentials: 'include' })
      if (res.status === 401) throw new UnauthorizedError()
      if (!res.ok) throw new Error(`Failed to load product-owner activity: ${res.status}`)
      const body = (await res.json()) as { days: ProductOwnerActivityDay[] }
      return expectArray<ProductOwnerActivityDay>(body.days, 'product-owner activity')
    },
    { initialValue: [] },
  )

  // `repo`/`hours` live outside the fetcher closure so `fetchActivityHourly`
  // can keep its existing (repo, hours) signature — useAsyncResource's
  // `refresh()` itself always takes no arguments.
  let hourlyRepo = ''
  let hourlyHours = 0
  const {
    data: activityHourlyHours,
    loaded: activityHourlyLoaded,
    loading: activityHourlyLoading,
    error: activityHourlyLoadError,
    needsAuth: activityHourlyNeedsAuth,
    refresh: refreshActivityHourly,
  } = useAsyncResource<ProductOwnerActivityHour[]>(
    async () => {
      const url = `/api/v1/agent/product_owner_runs/activity/hourly?repo=${encodeURIComponent(hourlyRepo)}&hours=${hourlyHours}`
      const res = await fetch(url, { credentials: 'include' })
      if (res.status === 401) throw new UnauthorizedError()
      if (!res.ok) throw new Error(`Failed to load hourly activity: ${res.status}`)
      const body = (await res.json()) as { repo: string; hours: ProductOwnerActivityHour[] }
      return expectArray<ProductOwnerActivityHour>(body.hours, 'product-owner hourly activity')
    },
    { initialValue: [] },
  )

  // `days` selects the window (7/30/90) — re-fetched whenever the tab's
  // selector changes, unlike fetchActivity's fixed 52-week lookback.
  async function fetchActivityHourly(repo: string, hours: number): Promise<void> {
    hourlyRepo = repo
    hourlyHours = hours
    return refreshActivityHourly()
  }

  let reviewDays = 0
  const reviewPerRepo = ref<ProductOwnerReviewRepoRow[]>([])
  const reviewAllRepos = ref<ProductOwnerReviewAggregateRow | null>(null)
  const reviewPerDay = ref<ProductOwnerReviewDay[]>([])
  const reviewCostPercentiles = ref<ProductOwnerReviewCostPercentiles | null>(null)
  const {
    data: reviewTotals,
    loaded: reviewLoaded,
    loading: reviewLoading,
    error: reviewLoadError,
    needsAuth: reviewNeedsAuth,
    refresh: refreshReview,
  } = useAsyncResource<ProductOwnerReviewTotals | null>(async () => {
    const res = await fetch(`/api/v1/agent/product_owner_runs/review?days=${reviewDays}`, { credentials: 'include' })
    if (res.status === 401) throw new UnauthorizedError()
    if (!res.ok) throw new Error(`Failed to load product-owner review: ${res.status}`)
    const body = (await res.json()) as {
      totals: ProductOwnerReviewTotals
      per_repo: ProductOwnerReviewRepoRow[]
      all_repos: ProductOwnerReviewAggregateRow
      per_day: ProductOwnerReviewDay[]
      cost_percentiles: ProductOwnerReviewCostPercentiles
    }
    reviewPerRepo.value = expectArray<ProductOwnerReviewRepoRow>(body.per_repo, 'product-owner review')
    reviewAllRepos.value = body.all_repos
    reviewPerDay.value = expectArray<ProductOwnerReviewDay>(body.per_day, 'product-owner review')
    reviewCostPercentiles.value = body.cost_percentiles
    return body.totals
  })

  async function fetchReview(days: number): Promise<void> {
    reviewDays = days
    return refreshReview()
  }

  return {
    runs,
    latestPerRepo,
    loading,
    loaded,
    needsAuth,
    loadError,
    fetchRuns,
    activityDays,
    activityLoading,
    activityLoaded,
    activityLoadError,
    activityNeedsAuth,
    fetchActivity,
    activityHourlyHours,
    activityHourlyLoading,
    activityHourlyLoaded,
    activityHourlyLoadError,
    activityHourlyNeedsAuth,
    fetchActivityHourly,
    reviewTotals,
    reviewPerRepo,
    reviewAllRepos,
    reviewPerDay,
    reviewCostPercentiles,
    reviewLoading,
    reviewLoaded,
    reviewLoadError,
    reviewNeedsAuth,
    fetchReview,
  }
})
